// Extracted from original/src/components/DollarVaultView.tsx (1,645 lines; ADR-08 §2.1 "copy, then cut logic"):
//   hero cards            :486-616 (cash :488-523, trading profits :525-547, customer trusts :549-571, rates :573-615)
//   segmented tabs        :618-660
//   trade sub-tab         :664-1077 (form :674-1018: buy / sell pills :685-707, counterparty :711-743, customer block
//                         :746-910, due date :913-927, mode alert :930-957, amount + rate :959-985, cost rate :988-1006,
//                         preview box :1009-1038, trust warning :1041-1063, note :1066-1077, submit :1080-1096;
//                         recent operations :1100-1160)
//   ledger sub-tab        :1163-1503 (toolbar :1173-1239, search + dates :1242-1330, table :1333-1424, mobile cards :1427-1500)
//   trusts sub-tab        :1506-1640
// Kept: every class string, icons and sizes, the motion / AnimatePresence frames, the open flag of the customer
// dropdown (pure UI state), the `$` / `د.ع` unit markup and the emoji of the old labels.
// Cut: `localDataService.getDebts()` and the `debts` filter (:131-141, :782-787, :482-483 → `customerOptions`,
// `trusts`); every `parseNumeric(...)` / `formatNumber(...)` and the breakdown arithmetic (:144-164, :878-907, the
// `usdAmount × rate` preview and `expectedProfit` :1009-1037) → preformatted labels from the server preview (ADR-08 §2.1
// item 2, ADR-02 §2.9); `handleSubmitForm` / `executeCustomerOp` (:168-395) → `onSubmit` (the gateway, D-232);
// `handleShareWhatsAppUSD` / `handleExportPDFUSD` / `handlePrintUSDTransactions` (:397-465) → `onShare` / `onPdf` /
// `onPrint`; `wallets.find(w => w.id === 11)` and `subSafeBalances[username]` (:476-479) → `cards.cashLabel`;
// `new Date().toISOString()` date presets (:1291-1309) → `onPresetToday` / `onPresetMonth` (office clock, D-064);
// `currentUser.role === 'admin' | 'super_manager'` (:582) → `cards.canEditRates` (D-126 token `settings_currencies`);
// `toLocaleString` / `toLocaleTimeString` of `created_at` → preformatted `dateTimeLabel` / `timeLabel`.
// Changed by decision: holders of `usd_direct_deposit_withdraw` get a small icon button on the cash card (the rates
// card's edit-button vocabulary, :582-593) that switches the form to `usd_direct_deposit` / `usd_direct_withdraw`
// (D-211; the old `dollarOpType === "direct"` existed in App.tsx but had no control — V-12); in that mode the buy /
// sell pills show unselected and a deposit / withdraw switch replaces the counterparty selector; the trust-link note
// says what the old handler actually did (App.tsx:46343, F-239); recent rows label every type instead of calling
// every non-buy a sale (:1121-1134). Added: data-testid attributes.
import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowDownUp, DollarSign, TrendingUp, Users, ArrowLeftRight, Printer, Search, Share2, FileText, Edit2, Shield, Coins, Clock, Plus } from 'lucide-react';
import { NumericInput } from '../components/NumericInput.js';
import { EnglishDatePicker } from '../components/EnglishDatePicker.js';
import { useT } from '../i18n-context.js';

export type DollarSubTab = 'trade' | 'ledger' | 'trusts';
export type DollarOpType = 'buy' | 'sell' | 'direct';
export type DollarDirectType = 'deposit' | 'withdraw';
export type DollarCounterparty = 'box' | 'customer';
export type DollarAllocation = 'debt' | 'trust';
/** Badge of a feed row (old :1376-1390): `cash` = a merged cash transfer, `settle` = any other USD operation. */
export type DollarRowKind = 'buy' | 'sell' | 'withdraw' | 'deposit' | 'cash' | 'settle';

export interface DollarCards {
  /** Old `canViewDollarVault` (permission `view_main_vault_usd`): vault card, else the viewer's USD custody card. */
  readonly canViewVault: boolean;
  readonly username: string;
  /** Formatted USD figure of the vault (own + custodies, D-118) or of the viewer's custody. */
  readonly cashLabel: string;
  /** Formatted IQD trading profit over the feed's current filter (old `totalUsdProfits`, :480). */
  readonly profitLabel: string;
  readonly trustsTotalLabel: string;
  readonly trustsCount: number;
  readonly buyRateLabel: string;
  readonly sellRateLabel: string;
  readonly canEditRates: boolean;
}

export interface DollarForm {
  readonly opType: DollarOpType;
  readonly directType: DollarDirectType;
  readonly counterparty: DollarCounterparty;
  /** Text of the customer field: a name typed or picked; `addNewLabel` when "+ add new account" was chosen. */
  readonly customerName: string;
  readonly customerId: string;
  readonly newCustomerName: string;
  readonly allocation: DollarAllocation;
  readonly receivedCash: string;
  readonly remainingOnAccount: string;
  readonly dueDate: string;
  readonly amount: string;
  readonly rate: string;
  readonly costRate: string;
  readonly note: string;
  readonly trustClientName: string;
}

export interface DollarCustomerOption {
  readonly id: string;
  readonly name: string;
  /** e.g. "1,250,000 د.ع (دين)" — formatted by the container (old :800-802). */
  readonly amountLabel: string;
}

/** Server preview figures (ADR-08 §2.1 item 2); null = box hidden (old: amount and rate not both > 0). */
export interface DollarPreview {
  readonly totalLabel: string;
  /** Sell only (old: when a cost rate is entered). */
  readonly profit?: { readonly label: string; readonly tone: 'negative' | 'positive' | 'zero' };
}

export interface DollarFeedRow {
  readonly id: string;
  readonly kind: DollarRowKind;
  /** Old `m.item`: the operation's title. */
  readonly title: string;
  readonly dateTimeLabel: string;
  readonly timeLabel: string;
  readonly note: string | null;
  /** Formatted absolute USD amount (old `usdVal`). */
  readonly usdLabel: string;
  /** Formatted IQD counter-value of a buy / sell, null otherwise (old `c_out` / `c_in`). */
  readonly iqdLabel: string | null;
  /** Formatted profit with its sign ("+5,000" / "-5,000"), null = "-" (old `comm`). */
  readonly profitLabel: string | null;
}

export interface DollarTrustRow {
  readonly id: string;
  readonly name: string;
  readonly usdLabel: string;
}

export interface DollarVaultProps {
  readonly cards: DollarCards;
  readonly subTab: DollarSubTab;
  readonly onSubTabChange: (tab: DollarSubTab) => void;
  readonly onEditRates: () => void;

  readonly form: DollarForm;
  readonly onFormChange: (patch: Partial<DollarForm>) => void;
  /** The container resets / pre-fills fields on a type switch (old :690, rate and cost pre-fill). */
  readonly onOpTypeChange: (op: DollarOpType) => void;
  /** Old :716-719: switching to the box clears the due date. */
  readonly onCounterpartyChange: (cp: DollarCounterparty) => void;
  /** D-211 holders only (`usd_direct_deposit_withdraw`). */
  readonly showDirect: boolean;
  readonly ratePlaceholder: string;
  readonly costPlaceholder: string;
  readonly customerOptions: readonly DollarCustomerOption[];
  readonly onCustomerPick: (option: DollarCustomerOption | 'new') => void;
  /** Old :878-907: editing one breakdown field recomputes the other (the container, from the server total). */
  readonly onCashReceivedChange: (value: string) => void;
  readonly onRemainingChange: (value: string) => void;
  readonly preview: DollarPreview | null;
  readonly canSubmit: boolean;
  readonly busy: boolean;
  readonly onSubmit: () => void;

  /** The newest rows (old `usdFeedMovements.slice(0, 5)`). */
  readonly recent: readonly DollarFeedRow[];
  readonly feed: readonly DollarFeedRow[];
  readonly selectedIds: readonly string[];
  readonly onSelectedChange: (ids: string[]) => void;
  readonly onShare: () => void;
  readonly onPdf: () => void;
  readonly onPrint: () => void;
  readonly search: string;
  readonly onSearchChange: (value: string) => void;
  readonly startDate: string;
  readonly endDate: string;
  readonly onStartDateChange: (value: string) => void;
  readonly onEndDateChange: (value: string) => void;
  readonly onPresetToday: () => void;
  readonly onPresetMonth: () => void;

  readonly trusts: readonly DollarTrustRow[];
  readonly onSellTrust: (id: string) => void;
}

const BADGE_CLASS: Readonly<Record<DollarRowKind, string>> = {
  cash: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30',
  buy: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30',
  sell: 'bg-blue-50 text-blue-600 dark:bg-blue-950/30',
  withdraw: 'bg-rose-50 text-rose-600 dark:bg-rose-950/30',
  deposit: 'bg-purple-50 text-purple-600 dark:bg-purple-950/30',
  settle: 'bg-purple-50 text-purple-600 dark:bg-purple-950/30',
};

export function DollarVault(props: DollarVaultProps) {
  const t = useT();
  const { cards, form, subTab } = props;
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const isBuy = form.opType === 'buy';
  const isSell = form.opType === 'sell';
  const isDirect = form.opType === 'direct';
  const addNew = t('dollar.add_new_account');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!props.busy && props.canSubmit) props.onSubmit();
  };

  const allSelected = props.selectedIds.length > 0 && props.selectedIds.length === props.feed.length;
  const toggle = (id: string, on: boolean) => props.onSelectedChange(on ? [...props.selectedIds, id] : props.selectedIds.filter((x) => x !== id));
  const hasRange = props.startDate !== '' || props.endDate !== '';

  // The alert box, pill colours and submit label of the direct pill (V-12) reuse the buy / sell vocabulary.
  const alertTone = isBuy || (isDirect && form.directType === 'deposit') ? 'buy' : 'sell';
  const modeTitle = isBuy ? t('dollar.mode_buy_title') : isSell ? t('dollar.mode_sell_title') : form.directType === 'deposit' ? t('dollar.mode_deposit_title') : t('dollar.mode_withdraw_title');
  const modeSub = isBuy ? t('dollar.mode_buy_sub') : isSell ? t('dollar.mode_sell_sub') : form.directType === 'deposit' ? t('dollar.mode_deposit_sub') : t('dollar.mode_withdraw_sub');
  const submitLabel = isBuy ? t('dollar.submit_buy') : isSell ? t('dollar.submit_sell') : form.directType === 'deposit' ? t('dollar.submit_deposit') : t('dollar.submit_withdraw');
  const badge = (k: DollarRowKind, mobile: boolean) => t(`dollar.badge_${mobile ? 'm_' : ''}${k}`);

  return (
    <motion.div
      key="dollar"
      data-testid="dollar-screen"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -15 }}
      className="space-y-5 pb-28 sm:pb-12"
    >
      {/* Hero Stats Cards Grid - 2x2 on Mobile, 4 columns on Large Screens */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
        {/* Physical Cash */}
        <div
          data-testid="dollar-card-cash"
          onClick={() => props.onSubTabChange('trade')}
          className={`premium-card p-3.5 sm:p-5 relative overflow-hidden group hover:border-emerald-500/40 transition-all cursor-pointer ${subTab === 'trade' ? 'ring-2 ring-emerald-500/50 bg-emerald-50/20 dark:bg-emerald-950/20' : ''}`}
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 rounded-full -mr-12 -mt-12 blur-2xl group-hover:scale-125 transition-transform" />
          <div className="flex justify-between items-center relative z-10 mb-2 sm:mb-3">
            <span className="text-[10px] sm:text-xs font-black text-gray-500 dark:text-[#8b949e] uppercase tracking-wider truncate">
              {cards.canViewVault ? t('dollar.cash_title_vault') : t('dollar.cash_title_sub')}
            </span>
            <div className="flex items-center gap-1.5">
              {props.showDirect && (
                <button
                  type="button"
                  data-testid="dollar-op-direct"
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onSubTabChange('trade');
                    props.onOpTypeChange('direct');
                  }}
                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-xl flex items-center justify-center font-bold transition-all shadow-sm cursor-pointer ${
                    isDirect ? 'bg-purple-600 text-white' : 'bg-purple-50 hover:bg-purple-100 dark:bg-purple-950/40 dark:hover:bg-purple-900/40 text-purple-600 dark:text-purple-400'
                  }`}
                  title={t('dollar.pill_direct')}
                >
                  <ArrowDownUp size={13} />
                </button>
              )}
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold border border-emerald-100 dark:border-emerald-900/30 shrink-0">
                <DollarSign size={18} className="sm:w-5 sm:h-5" />
              </div>
            </div>
          </div>
          <div className="relative z-10">
            <div className="text-lg sm:text-2xl font-black text-emerald-600 dark:text-emerald-400 font-mono tracking-tight dir-ltr text-right">
              {cards.cashLabel} $
            </div>
            <p className="text-[9px] sm:text-[10px] text-gray-400 dark:text-[#8b949e] mt-1 font-bold truncate">
              {cards.canViewVault ? t('dollar.cash_sub_vault') : t('dollar.cash_sub_custody', { username: cards.username })}
            </p>
          </div>
        </div>

        {/* Trading Profits */}
        <div
          data-testid="dollar-card-profits"
          onClick={() => props.onSubTabChange('ledger')}
          className={`premium-card p-3.5 sm:p-5 relative overflow-hidden group hover:border-blue-500/40 transition-all cursor-pointer ${subTab === 'ledger' ? 'ring-2 ring-blue-500/50 bg-blue-50/20 dark:bg-blue-950/20' : ''}`}
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/10 rounded-full -mr-12 -mt-12 blur-2xl group-hover:scale-125 transition-transform" />
          <div className="flex justify-between items-center relative z-10 mb-2 sm:mb-3">
            <span className="text-[10px] sm:text-xs font-black text-gray-500 dark:text-[#8b949e] uppercase tracking-wider truncate">
              {t('dollar.profits_title')}
            </span>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold border border-blue-100 dark:border-blue-900/30 shrink-0">
              <TrendingUp size={18} className="sm:w-5 sm:h-5" />
            </div>
          </div>
          <div className="relative z-10">
            <div className="text-lg sm:text-2xl font-black text-blue-600 dark:text-blue-400 font-mono tracking-tight dir-ltr text-right">
              +{cards.profitLabel} <span className="text-xs font-sans">{t('dollar.iqd')}</span>
            </div>
            <p className="text-[9px] sm:text-[10px] text-gray-400 dark:text-[#8b949e] mt-1 font-bold truncate">
              {t('dollar.profits_sub')}
            </p>
          </div>
        </div>

        {/* Customer Trusts */}
        <div
          data-testid="dollar-card-trusts"
          onClick={() => props.onSubTabChange('trusts')}
          className={`premium-card p-3.5 sm:p-5 relative overflow-hidden group hover:border-amber-500/40 transition-all cursor-pointer ${subTab === 'trusts' ? 'ring-2 ring-amber-500/50 bg-amber-50/20 dark:bg-amber-950/20' : ''}`}
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/10 rounded-full -mr-12 -mt-12 blur-2xl group-hover:scale-125 transition-transform" />
          <div className="flex justify-between items-center relative z-10 mb-2 sm:mb-3">
            <span className="text-[10px] sm:text-xs font-black text-gray-500 dark:text-[#8b949e] uppercase tracking-wider truncate">
              {t('dollar.trusts_title')}
            </span>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-amber-50 dark:bg-amber-950/40 flex items-center justify-center text-amber-600 dark:text-amber-400 font-bold border border-amber-100 dark:border-amber-900/30 shrink-0">
              <Users size={18} className="sm:w-5 sm:h-5" />
            </div>
          </div>
          <div className="relative z-10">
            <div className="text-lg sm:text-2xl font-black text-amber-600 dark:text-amber-400 font-mono tracking-tight dir-ltr text-right">
              {cards.trustsTotalLabel} $
            </div>
            <p className="text-[9px] sm:text-[10px] text-gray-400 dark:text-[#8b949e] mt-1 font-bold truncate">
              {t('dollar.trusts_active', { n: cards.trustsCount })}
            </p>
          </div>
        </div>

        {/* Operational Exchange Rates */}
        <div
          data-testid="dollar-card-rates"
          onClick={() => props.onSubTabChange('trade')}
          className="premium-card p-3.5 sm:p-5 relative overflow-hidden group hover:border-purple-500/40 transition-all cursor-pointer col-span-2 sm:col-span-1"
        >
          <div className="flex justify-between items-center mb-1.5 sm:mb-2">
            <span className="text-[10px] sm:text-xs font-black text-gray-500 dark:text-[#8b949e] uppercase tracking-wider">
              {t('dollar.rates_title')}
            </span>
            <div className="flex items-center gap-1.5">
              {cards.canEditRates && (
                <button
                  type="button"
                  data-testid="dollar-edit-rates"
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onEditRates();
                  }}
                  className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/40 dark:hover:bg-blue-900/40 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold transition-all shadow-sm cursor-pointer"
                  title={t('dollar.rates_edit')}
                >
                  <Edit2 size={13} />
                </button>
              )}
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-purple-50 dark:bg-purple-950/40 flex items-center justify-center text-purple-600 dark:text-purple-400 font-bold">
                <ArrowLeftRight size={15} />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-1.5 text-center pt-1 border-t border-gray-100 dark:border-gray-800">
            <div className="bg-emerald-500/10 p-1.5 sm:p-2 rounded-xl">
              <div className="text-[9px] sm:text-[10px] text-emerald-600 dark:text-emerald-400 font-extrabold">{t('dollar.buy')}</div>
              <div className="text-xs sm:text-sm font-black text-emerald-700 dark:text-emerald-300 font-mono mt-0.5">{cards.buyRateLabel}</div>
            </div>
            <div className="bg-blue-500/10 p-1.5 sm:p-2 rounded-xl">
              <div className="text-[9px] sm:text-[10px] text-blue-600 dark:text-blue-400 font-extrabold">{t('dollar.sell')}</div>
              <div className="text-xs sm:text-sm font-black text-blue-700 dark:text-blue-300 font-mono mt-0.5">{cards.sellRateLabel}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Touch-Friendly Responsive Segmented Navigation Bar */}
      <div className="p-1 bg-slate-100 dark:bg-[#161b22]/80 rounded-2xl border border-slate-200/80 dark:border-slate-800/80 w-full sm:w-fit rtl:mr-0 rtl:ml-auto ltr:ml-0 ltr:mr-auto shadow-xs grid grid-cols-3 sm:flex sm:items-center gap-1">
        {([
          ['trade', <ArrowLeftRight key="i" size={15} className="shrink-0" />],
          ['ledger', <Printer key="i" size={15} className="shrink-0" />],
          ['trusts', <Users key="i" size={15} className="shrink-0" />],
        ] as const).map(([tab, icon]) => (
          <button
            key={tab}
            type="button"
            data-testid={`dollar-tab-${tab}`}
            onClick={() => props.onSubTabChange(tab)}
            className={`flex items-center justify-center gap-1.5 py-2.5 sm:py-2 px-2 sm:px-4 rounded-xl text-xs font-bold transition-all duration-150 cursor-pointer min-h-[40px] ${
              subTab === tab
                ? 'bg-white dark:bg-[#0d1117] text-emerald-600 dark:text-emerald-400 shadow-sm border border-slate-200/50 dark:border-slate-700/60 font-black'
                : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
            }`}
          >
            {icon}
            <span className="hidden sm:inline">{t(`dollar.tab_${tab}`)}</span>
            <span className="sm:hidden text-[11px] font-black">{t(`dollar.tab_${tab}_short`)}</span>
          </button>
        ))}
      </div>

      {/* SubTab Views */}
      <AnimatePresence mode="wait">
        {subTab === 'trade' && (
          <motion.div
            key="subtab-trade"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="w-full grid grid-cols-1 lg:grid-cols-12 gap-6"
          >
            {/* Trade Execution Form */}
            <div className="lg:col-span-7 premium-card p-6 space-y-6 border border-gray-150 dark:border-[#30363d] shadow-lg">
              <div className="flex items-center justify-between border-b border-gray-100 dark:border-[#30363d] pb-4">
                <div>
                  <h3 className="text-base font-black text-gray-900 dark:text-[#f0f6fc] uppercase tracking-wider flex items-center gap-2">
                    <span>💵</span>
                    <span>{t('dollar.trade_title')}</span>
                  </h3>
                  <p className="text-xs text-gray-400 font-bold mt-1">{t('dollar.trade_sub')}</p>
                </div>

                {/* Buy / Sell Toggle Pills */}
                <div className="flex bg-gray-100 dark:bg-[#161b22] p-1 rounded-xl border border-gray-200 dark:border-[#30363d]">
                  <button
                    type="button"
                    data-testid="dollar-op-buy"
                    onClick={() => props.onOpTypeChange('buy')}
                    className={`px-4 py-2 rounded-lg text-xs font-black transition-all cursor-pointer ${
                      isBuy ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    {t('dollar.pill_buy')}
                  </button>
                  <button
                    type="button"
                    data-testid="dollar-op-sell"
                    onClick={() => props.onOpTypeChange('sell')}
                    className={`px-4 py-2 rounded-lg text-xs font-black transition-all cursor-pointer ${
                      isSell ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    {t('dollar.pill_sell')}
                  </button>
                </div>
              </div>

              <form onSubmit={submit} className="space-y-5">
                {/* Counterparty Selector: Direct Box vs Customer Account */}
                {!isDirect && (
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">{t('dollar.counterparty')}</label>
                    <div className="grid grid-cols-2 p-1 bg-slate-100 dark:bg-white/5 rounded-2xl border border-slate-200/50 dark:border-white/5">
                      <button
                        type="button"
                        data-testid="dollar-cp-box"
                        onClick={() => props.onCounterpartyChange('box')}
                        className={`py-2.5 px-2 rounded-xl text-xs font-black transition-all cursor-pointer ${
                          form.counterparty === 'box' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                        }`}
                      >
                        {t('dollar.cp_box')}
                      </button>
                      <button
                        type="button"
                        data-testid="dollar-cp-customer"
                        onClick={() => props.onCounterpartyChange('customer')}
                        className={`py-2.5 px-2 rounded-xl text-xs font-black transition-all cursor-pointer ${
                          form.counterparty === 'customer' ? 'bg-amber-600 text-white shadow-md' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                        }`}
                      >
                        {t('dollar.cp_customer')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Direct deposit / withdraw switch (D-211, V-12), same vocabulary as the counterparty selector */}
                {isDirect && (
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">{t('dollar.direct_kind')}</label>
                    <div className="grid grid-cols-2 p-1 bg-slate-100 dark:bg-white/5 rounded-2xl border border-slate-200/50 dark:border-white/5">
                      {(['deposit', 'withdraw'] as const).map((d) => (
                        <button
                          key={d}
                          type="button"
                          data-testid={`dollar-direct-${d}`}
                          onClick={() => props.onFormChange({ directType: d })}
                          className={`py-2.5 px-2 rounded-xl text-xs font-black transition-all cursor-pointer ${
                            form.directType === d ? 'bg-purple-600 text-white shadow-md' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                          }`}
                        >
                          {t(`dollar.direct_${d}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Customer Account Fields (if counterParty === 'customer') */}
                {!isDirect && form.counterparty === 'customer' && (
                  <div className="bg-amber-500/5 dark:bg-amber-500/10 p-4 rounded-2xl border border-amber-500/20 space-y-3.5 animate-in fade-in duration-200">
                    <div className="text-xs font-black text-amber-700 dark:text-amber-400 flex items-center gap-1.5 uppercase tracking-wide">
                      <Users size={15} />
                      <span>{t('dollar.customer_details')}</span>
                    </div>

                    {/* Customer Selector Dropdown */}
                    <div className="relative">
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{t('dollar.customer_name')}</label>
                      <input
                        type="text"
                        data-testid="dollar-customer-name"
                        value={form.customerName || ''}
                        onFocus={() => setShowCustomerDropdown(true)}
                        onChange={(e) => {
                          props.onFormChange({ customerName: e.target.value, customerId: '' });
                          setShowCustomerDropdown(true);
                        }}
                        placeholder={t('dollar.customer_placeholder')}
                        className="w-full bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] focus:ring-2 focus:ring-amber-500/20 rounded-xl px-3.5 py-2.5 font-bold text-xs"
                        autoComplete="off"
                      />

                      {showCustomerDropdown && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setShowCustomerDropdown(false)} />
                          <div className="absolute top-full left-0 right-0 mt-1.5 bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] rounded-2xl shadow-xl z-50 max-h-[200px] overflow-y-auto p-1 space-y-1">
                            <button
                              type="button"
                              onClick={() => {
                                props.onCustomerPick('new');
                                setShowCustomerDropdown(false);
                              }}
                              className="w-full text-right px-3 py-2 text-xs font-black text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 rounded-xl transition-all flex items-center justify-between cursor-pointer"
                            >
                              <span>{addNew}</span>
                              <Plus size={14} />
                            </button>
                            {props.customerOptions
                              .filter((d) => !form.customerName || form.customerName === addNew || d.name.toLowerCase().includes(form.customerName.toLowerCase()))
                              .map((d) => (
                                <button
                                  key={d.id}
                                  type="button"
                                  onClick={() => {
                                    props.onCustomerPick(d);
                                    setShowCustomerDropdown(false);
                                  }}
                                  className="w-full text-right px-3 py-2 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#161b22] rounded-xl transition-all flex items-center justify-between cursor-pointer gap-2"
                                >
                                  <span className="truncate">{d.name}</span>
                                  <span className="font-mono text-[10px] text-slate-400 bg-slate-50 dark:bg-[#161b22] px-2 py-0.5 rounded-md border border-slate-100 dark:border-[#30363d] shrink-0">
                                    {d.amountLabel}
                                  </span>
                                </button>
                              ))}
                          </div>
                        </>
                      )}
                    </div>

                    {form.customerName === addNew && (
                      <div className="space-y-1">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">{t('dollar.new_account_name')}</label>
                        <input
                          type="text"
                          required
                          value={form.newCustomerName || ''}
                          onChange={(e) => props.onFormChange({ newCustomerName: e.target.value })}
                          placeholder={t('dollar.new_account_placeholder')}
                          className="w-full bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-3.5 py-2.5 font-bold text-xs"
                        />
                      </div>
                    )}

                    {/* Allocation Type */}
                    <div className="space-y-1">
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">{t('dollar.account_type')}</label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            props.onFormChange({ allocation: 'debt', customerName: '', customerId: '' });
                            setShowCustomerDropdown(true);
                          }}
                          className={`py-2 px-3 rounded-xl flex items-center justify-center gap-1.5 border text-xs transition-all cursor-pointer ${
                            form.allocation === 'debt'
                              ? 'bg-rose-500/10 border-rose-500/40 text-rose-600 dark:text-rose-400 font-black'
                              : 'bg-white dark:bg-[#05070a] border-gray-200 dark:border-[#30363d] text-slate-400 font-bold'
                          }`}
                        >
                          <TrendingUp size={14} className={form.allocation === 'debt' ? 'text-rose-600 dark:text-rose-400' : 'text-rose-500'} />
                          <span>{t('dollar.debts')}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            props.onFormChange({ allocation: 'trust', customerName: '', customerId: '' });
                            setShowCustomerDropdown(true);
                          }}
                          className={`py-2 px-3 rounded-xl flex items-center justify-center gap-1.5 border text-xs transition-all cursor-pointer ${
                            form.allocation === 'trust'
                              ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-600 dark:text-emerald-400 font-black'
                              : 'bg-white dark:bg-[#05070a] border-gray-200 dark:border-[#30363d] text-slate-400 font-bold'
                          }`}
                        >
                          <Shield size={14} className={form.allocation === 'trust' ? 'text-emerald-600 dark:text-emerald-400' : 'text-emerald-500'} />
                          <span>{t('dollar.trusts')}</span>
                        </button>
                      </div>
                    </div>

                    {/* Payment Breakdown */}
                    <div className="space-y-2 pt-1 border-t border-amber-500/15">
                      <div className="text-[10px] font-black text-amber-800 dark:text-amber-300 flex items-center gap-1">
                        <Coins size={13} />
                        <span>{t('dollar.breakdown')}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2.5">
                        <div>
                          <label className="block text-[9px] font-bold text-slate-400 mb-1 uppercase">{t('dollar.cash_received')}</label>
                          <NumericInput
                            value={form.receivedCash ?? ''}
                            onChange={props.onCashReceivedChange}
                            placeholder="0"
                            className="w-full bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-2 text-center font-mono font-bold text-xs focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                          />
                        </div>
                        <div>
                          <label className="block text-[9px] font-bold text-slate-400 mb-1 uppercase">{t('dollar.on_account')}</label>
                          <NumericInput
                            value={form.remainingOnAccount ?? ''}
                            onChange={props.onRemainingChange}
                            placeholder="0"
                            className="w-full bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-2 text-center font-mono font-bold text-xs focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Due Date Input (Only for Customer Account) */}
                {!isDirect && form.counterparty === 'customer' && (
                  <div className="space-y-2.5 bg-amber-500/5 dark:bg-amber-500/10 p-3.5 rounded-2xl border border-amber-500/20 dark:border-amber-500/30 animate-in fade-in duration-200">
                    <div className="flex items-center justify-between text-xs font-black text-amber-800 dark:text-amber-300">
                      <span className="flex items-center gap-1.5">
                        <Clock size={15} />
                        <span>{t('dollar.due_date')}</span>
                      </span>
                    </div>
                    <EnglishDatePicker value={form.dueDate || ''} onChange={(val) => props.onFormChange({ dueDate: val })} className="!py-2 !text-xs !bg-white dark:!bg-[#05070a]" />
                  </div>
                )}

                {/* Operational Mode Alert */}
                <div
                  className={`p-4 rounded-2xl border flex items-center justify-between ${
                    alertTone === 'buy' ? 'bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900/30' : 'bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900/30'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-lg ${alertTone === 'buy' ? 'bg-emerald-500/20 text-emerald-600' : 'bg-blue-500/20 text-blue-600'}`}>
                      {alertTone === 'buy' ? '📥' : '📤'}
                    </div>
                    <div>
                      <h4 className="text-xs font-black text-gray-900 dark:text-[#f0f6fc]">{modeTitle}</h4>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 font-bold mt-0.5">{modeSub}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* USD Amount */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-black text-gray-700 dark:text-[#f0f6fc]">{t('dollar.amount')}</label>
                    <NumericInput
                      value={form.amount ?? ''}
                      onChange={(v) => props.onFormChange({ amount: v })}
                      placeholder="1,000"
                      className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-2xl px-4 py-3 font-mono font-bold text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:text-[#f0f6fc]"
                    />
                  </div>

                  {/* Exchange Rate (buy / sell) — the cost rate of a direct deposit (D-211) in the same slot */}
                  {!(isDirect && form.directType === 'withdraw') && (
                    <div className="space-y-1.5">
                      <label className="block text-xs font-black text-gray-700 dark:text-[#f0f6fc]">{isDirect ? t('dollar.deposit_cost') : t('dollar.rate')}</label>
                      <NumericInput
                        value={(isDirect ? form.costRate : form.rate) ?? ''}
                        onChange={(v) => props.onFormChange(isDirect ? { costRate: v } : { rate: v })}
                        placeholder={props.ratePlaceholder}
                        className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-2xl px-4 py-3 font-mono font-bold text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:text-[#f0f6fc]"
                      />
                    </div>
                  )}
                </div>

                {/* Cost Rate for Sell Operations */}
                {isSell && (
                  <div className="space-y-1.5 bg-blue-50/30 dark:bg-blue-950/10 p-3.5 rounded-2xl border border-blue-100 dark:border-blue-900/20">
                    <div className="flex items-center justify-between">
                      <label className="block text-xs font-black text-blue-900 dark:text-blue-300">{t('dollar.cost_rate')}</label>
                      <span className="text-[10px] text-blue-600 dark:text-blue-400 font-bold">{t('dollar.cost_hint')}</span>
                    </div>
                    <NumericInput
                      value={form.costRate ?? ''}
                      onChange={(v) => props.onFormChange({ costRate: v })}
                      placeholder={props.costPlaceholder}
                      className="w-full bg-white dark:bg-[#05070a] border border-blue-200 dark:border-blue-900/40 rounded-xl px-4 py-2.5 font-mono font-bold text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc]"
                    />
                  </div>
                )}

                {/* Calculation Preview Box */}
                {!isDirect && props.preview && (
                  <div data-testid="dollar-preview" className="p-3.5 sm:p-4 bg-slate-900 text-white dark:bg-black rounded-2xl space-y-2.5 text-xs border border-slate-800 shadow-inner">
                    <div className="flex justify-between items-center text-slate-300">
                      <span className="font-extrabold text-[11px] sm:text-xs">{t('dollar.equivalent')}</span>
                      <span className="font-black text-xl sm:text-2xl text-emerald-400 font-mono tracking-tight dir-ltr text-right">
                        {props.preview.totalLabel} <span className="text-xs font-sans text-slate-400">{t('dollar.iqd')}</span>
                      </span>
                    </div>
                    {isSell && props.preview.profit && (
                      <div
                        className={`flex justify-between items-center pt-2 border-t border-slate-800 font-extrabold ${
                          props.preview.profit.tone === 'negative' ? 'text-rose-400' : props.preview.profit.tone === 'positive' ? 'text-amber-400' : 'text-slate-400'
                        }`}
                      >
                        <span className="text-[11px] sm:text-xs">{t('dollar.expected_profit')}</span>
                        <span className="font-black text-base sm:text-lg font-mono inline-flex items-center gap-1 dir-ltr">
                          <span>{props.preview.profit.label}</span>
                          <span className="text-xs font-sans text-slate-400">{t('dollar.iqd')}</span>
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Trust Connected Warning */}
                {isSell && form.trustClientName && (
                  <div className="p-3.5 sm:p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/35 rounded-2xl space-y-1.5 text-right transition-all">
                    <div className="flex justify-between items-center text-xs text-amber-800 dark:text-amber-300 font-black">
                      <span className="flex items-center gap-1.5">
                        <span className="text-base">⚠️</span>
                        <span>{t('dollar.trust_linked', { name: form.trustClientName })}</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => props.onFormChange({ trustClientName: '' })}
                        className="text-amber-600 hover:text-amber-800 font-extrabold text-[11px] underline cursor-pointer min-h-[36px] flex items-center"
                      >
                        {t('dollar.unlink')}
                      </button>
                    </div>
                    <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80 font-bold leading-relaxed">{t('dollar.trust_linked_note', { name: form.trustClientName })}</p>
                  </div>
                )}

                {/* Statement / Note (the mandatory reason of a direct operation, D-211) */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-black text-gray-700 dark:text-[#f0f6fc]">{isDirect ? t('dollar.reason_label') : t('dollar.note_label')}</label>
                  <input
                    type="text"
                    data-testid="dollar-note"
                    value={form.note || ''}
                    onChange={(e) => props.onFormChange({ note: e.target.value })}
                    placeholder={t('dollar.note_placeholder')}
                    className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-2xl px-4 py-3 font-bold text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:text-[#f0f6fc]"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  data-testid="dollar-submit"
                  disabled={props.busy || !props.canSubmit}
                  className={`w-full py-3.5 sm:py-4 rounded-2xl font-black text-sm sm:text-base text-white transition-all shadow-lg min-h-[48px] cursor-pointer ${
                    alertTone === 'buy'
                      ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20 active:scale-[0.99]'
                      : 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/20 active:scale-[0.99]'
                  } disabled:opacity-50 flex items-center justify-center gap-2`}
                >
                  {props.busy ? t('dollar.processing') : submitLabel}
                </button>
              </form>
            </div>

            {/* Recent Operations Widget */}
            <div className="lg:col-span-5 space-y-6">
              <div className="premium-card p-5 space-y-4 border border-gray-150 dark:border-[#30363d]">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-black text-gray-700 dark:text-[#f0f6fc] uppercase tracking-wider flex items-center gap-2">
                    <span>🕒</span>
                    <span>{t('dollar.recent_title')}</span>
                  </h4>
                  <button type="button" onClick={() => props.onSubTabChange('ledger')} className="text-[11px] text-blue-600 dark:text-blue-400 font-bold hover:underline cursor-pointer">
                    {t('dollar.full_ledger')}
                  </button>
                </div>

                {props.recent.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-6 font-bold">{t('dollar.recent_empty')}</p>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-[#30363d]">
                    {props.recent.map((m) => {
                      const buyish = m.kind === 'buy' || m.kind === 'deposit';
                      return (
                        <div key={m.id} className="py-3 flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2.5">
                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-black ${buyish ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40' : 'bg-blue-50 text-blue-600 dark:bg-blue-950/40'}`}>
                              {buyish ? '📥' : '📤'}
                            </div>
                            <div>
                              <div className="font-extrabold text-gray-900 dark:text-[#f0f6fc]">{m.title}</div>
                              <div className="text-[10px] text-gray-400 font-bold">{m.timeLabel}</div>
                            </div>
                          </div>
                          <div className="text-left font-mono">
                            <div className="font-black text-emerald-600 dark:text-emerald-400">${m.usdLabel}</div>
                            {m.profitLabel && m.kind === 'sell' ? (
                              <div className="text-[10px] text-emerald-500 font-extrabold">
                                {m.profitLabel} {t('dollar.iqd')}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {subTab === 'ledger' && (
          <motion.div
            key="subtab-ledger"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="w-full space-y-6"
          >
            <div className="premium-card p-4 sm:p-6 space-y-4 sm:space-y-5 border border-gray-150 dark:border-[#30363d] shadow-lg">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                  <h4 className="text-sm sm:text-base font-black text-gray-900 dark:text-[#f0f6fc] uppercase tracking-wider flex items-center gap-2">
                    <span>📊</span>
                    <span>{t('dollar.ledger_title')}</span>
                  </h4>
                  <p className="text-[11px] sm:text-xs text-gray-400 font-bold mt-0.5">{t('dollar.ledger_sub')}</p>
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
                  {props.selectedIds.length > 0 && (
                    <span className="text-[10px] sm:text-[11px] font-black text-emerald-700 dark:text-emerald-400 bg-emerald-100/80 dark:bg-emerald-900/60 px-2.5 py-1 rounded-full border border-emerald-300/50 dark:border-emerald-700/50">
                      {t('dollar.selected', { n: props.selectedIds.length })}
                    </span>
                  )}

                  <div className="flex items-center gap-1.5 sm:gap-2 bg-emerald-50/80 dark:bg-[#161b22] border border-emerald-200/80 dark:border-[#30363d] p-1 rounded-2xl shadow-xs w-full sm:w-auto justify-center">
                    <button
                      type="button"
                      data-testid="dollar-share"
                      onClick={props.onShare}
                      className="flex-1 sm:flex-initial flex items-center justify-center gap-1 px-3 py-2 text-xs font-bold text-slate-700 dark:text-[#f0f6fc] hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40 rounded-xl transition-all cursor-pointer min-h-[38px] active:scale-95"
                      title={t('dollar.share_title')}
                    >
                      <Share2 size={16} />
                      <span className="text-[11px] font-black">{t('dollar.whatsapp')}</span>
                    </button>

                    <button
                      type="button"
                      data-testid="dollar-pdf"
                      onClick={props.onPdf}
                      className="flex-1 sm:flex-initial flex items-center justify-center gap-1 px-3 py-2 text-xs font-bold text-slate-700 dark:text-[#f0f6fc] hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40 rounded-xl transition-all cursor-pointer min-h-[38px] active:scale-95"
                      title={t('dollar.pdf_title')}
                    >
                      <FileText size={16} />
                      <span className="text-[11px] font-black">PDF</span>
                    </button>

                    <button
                      type="button"
                      data-testid="dollar-print"
                      onClick={props.onPrint}
                      className="flex-1 sm:flex-initial flex items-center justify-center gap-1 px-3 py-2 text-xs font-bold text-slate-700 dark:text-[#f0f6fc] hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40 rounded-xl transition-all cursor-pointer min-h-[38px] active:scale-95"
                      title={t('dollar.print_title')}
                    >
                      <Printer size={16} />
                      <span className="text-[11px] font-black">{t('dollar.print')}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Search & Date Range Filter Bar */}
              <div className="space-y-3">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-center">
                  <div className="relative lg:col-span-6">
                    <input
                      type="text"
                      data-testid="dollar-search"
                      value={props.search || ''}
                      onChange={(e) => props.onSearchChange(e.target.value)}
                      className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-2xl pl-10 pr-4 py-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:text-[#f0f6fc] font-bold"
                      placeholder={t('dollar.search_placeholder')}
                    />
                    <Search size={16} className="absolute left-3.5 top-3 text-gray-400 dark:text-[#8b949e]" />
                  </div>

                  <div className="lg:col-span-6 flex flex-wrap items-center gap-2">
                    <div className="flex-1 min-w-[130px] flex items-center gap-1.5 bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-2xl px-3 py-1.5">
                      <span className="text-[10px] font-black text-gray-400 whitespace-nowrap">{t('dollar.from')}</span>
                      <EnglishDatePicker value={props.startDate || ''} onChange={props.onStartDateChange} className="!py-1 !px-1.5 !text-xs !bg-transparent !border-0 focus:!ring-0 text-right" />
                    </div>

                    <div className="flex-1 min-w-[130px] flex items-center gap-1.5 bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-2xl px-3 py-1.5">
                      <span className="text-[10px] font-black text-gray-400 whitespace-nowrap">{t('dollar.to')}</span>
                      <EnglishDatePicker value={props.endDate || ''} onChange={props.onEndDateChange} className="!py-1 !px-1.5 !text-xs !bg-transparent !border-0 focus:!ring-0 text-right" />
                    </div>
                  </div>
                </div>

                {/* Quick Date Presets & Active Range Banner */}
                <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-gray-100 dark:border-[#30363d]/50 text-xs font-bold">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[10px] text-gray-400 font-bold ml-1">{t('dollar.quick_filter')}</span>
                    <button
                      type="button"
                      onClick={props.onPresetToday}
                      className="px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-[#161b22] hover:bg-emerald-500/10 hover:text-emerald-600 dark:hover:text-emerald-400 text-gray-600 dark:text-gray-300 text-[11px] font-extrabold transition-all cursor-pointer"
                    >
                      {t('dollar.today')}
                    </button>
                    <button
                      type="button"
                      onClick={props.onPresetMonth}
                      className="px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-[#161b22] hover:bg-emerald-500/10 hover:text-emerald-600 dark:hover:text-emerald-400 text-gray-600 dark:text-gray-300 text-[11px] font-extrabold transition-all cursor-pointer"
                    >
                      {t('dollar.this_month')}
                    </button>
                    {hasRange && (
                      <button
                        type="button"
                        onClick={() => {
                          props.onStartDateChange('');
                          props.onEndDateChange('');
                        }}
                        className="px-2.5 py-1 rounded-lg bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-400 text-[11px] font-extrabold hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-all cursor-pointer"
                      >
                        {t('dollar.clear_filter')}
                      </button>
                    )}
                  </div>

                  {hasRange && (
                    <div className="text-[11px] text-emerald-600 dark:text-emerald-400 font-mono font-black bg-emerald-50 dark:bg-emerald-950/30 px-3 py-1 rounded-lg border border-emerald-200/50 dark:border-emerald-800/30">
                      {t('dollar.period', { from: props.startDate || t('dollar.period_start'), to: props.endDate || t('dollar.period_today') })}
                    </div>
                  )}
                </div>
              </div>

              {/* Desktop Ledger Table View */}
              <div className="hidden md:block border border-gray-150 dark:border-[#30363d] rounded-2xl overflow-hidden bg-white dark:bg-[#0d1117] min-h-[350px] max-h-[500px] overflow-y-auto custom-scrollbar">
                <table className="w-full text-right text-xs" data-testid="dollar-feed">
                  <thead className="bg-gray-50 dark:bg-[#161b22] text-gray-500 dark:text-[#8b949e] font-black uppercase text-[10px] tracking-wider sticky top-0 z-10 border-b border-gray-200 dark:border-[#30363d]">
                    <tr>
                      <th className="p-3 w-10 text-center">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={(e) => props.onSelectedChange(e.target.checked ? props.feed.map((m) => m.id) : [])}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 h-3.5 w-3.5"
                        />
                      </th>
                      <th className="p-3">{t('dollar.col_details')}</th>
                      <th className="p-3">{t('dollar.col_type')}</th>
                      <th className="p-3 text-center">{t('dollar.col_usd')}</th>
                      <th className="p-3 text-center">{t('dollar.col_iqd')}</th>
                      <th className="p-3 text-center">{t('dollar.col_profit')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-[#30363d] font-bold">
                    {props.feed.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-12 text-center text-gray-400 font-bold">{t('dollar.empty_feed')}</td>
                      </tr>
                    ) : (
                      props.feed.map((m) => (
                        <tr key={m.id} className="hover:bg-gray-50/50 dark:hover:bg-[#161b22]/50 transition-colors">
                          <td className="p-3 text-center">
                            <input
                              type="checkbox"
                              checked={props.selectedIds.includes(m.id)}
                              onChange={(e) => toggle(m.id, e.target.checked)}
                              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 h-3.5 w-3.5"
                            />
                          </td>
                          <td className="p-3">
                            <div className="font-extrabold text-gray-900 dark:text-[#f0f6fc]">{m.title}</div>
                            <div className="text-[10px] text-gray-400 mt-0.5 font-mono">
                              {m.dateTimeLabel}
                              {m.note && ` | ${m.note}`}
                            </div>
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black ${BADGE_CLASS[m.kind]}`}>{badge(m.kind, false)}</span>
                          </td>
                          <td className="p-3 text-center font-black font-mono dark:text-[#f0f6fc]">${m.usdLabel}</td>
                          <td className="p-3 text-center font-bold text-gray-500 dark:text-gray-400 font-mono text-xs">{m.iqdLabel ?? '-'}</td>
                          <td className="p-3 text-center font-black font-mono text-emerald-600 dark:text-emerald-400">{m.profitLabel ? `${m.profitLabel} ${t('dollar.iqd')}` : '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards View - Formatted for mobile screens */}
              <div className="block md:hidden space-y-3">
                {props.feed.length === 0 ? (
                  <div className="p-8 text-center text-gray-400 font-bold bg-white dark:bg-[#0d1117] rounded-2xl border border-gray-150 dark:border-[#30363d]">{t('dollar.empty_feed')}</div>
                ) : (
                  props.feed.map((m) => {
                    const selected = props.selectedIds.includes(m.id);
                    return (
                      <div
                        key={m.id}
                        className={`p-3.5 bg-white dark:bg-[#0d1117] rounded-2xl border transition-all ${selected ? 'border-emerald-500 bg-emerald-50/10 dark:bg-emerald-950/20' : 'border-slate-200/80 dark:border-[#30363d]'}`}
                      >
                        <div className="flex justify-between items-start gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(e) => toggle(m.id, e.target.checked)}
                              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 h-4 w-4 shrink-0"
                            />
                            <div>
                              <div className="font-black text-xs text-gray-900 dark:text-[#f0f6fc]">{m.title}</div>
                              <div className="text-[10px] text-gray-400 font-mono mt-0.5">{m.dateTimeLabel}</div>
                            </div>
                          </div>

                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-black shrink-0 ${BADGE_CLASS[m.kind]}`}>{badge(m.kind, true)}</span>
                        </div>

                        {/* Digital Amount & Profit Box */}
                        <div className="p-3 bg-slate-900 text-white dark:bg-black rounded-xl border border-slate-800 my-2 space-y-1.5 font-mono">
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] text-slate-400 font-sans">{t('dollar.m_usd')}</span>
                            <span className="text-base font-black text-emerald-400 dir-ltr text-right">${m.usdLabel}</span>
                          </div>
                          {m.iqdLabel && (
                            <div className="flex justify-between items-center pt-1 border-t border-slate-800 text-[11px]">
                              <span className="text-[10px] text-slate-400 font-sans">{t('dollar.m_equiv')}</span>
                              <span className="font-bold text-slate-200 dir-ltr text-right">
                                {m.iqdLabel} {t('dollar.iqd')}
                              </span>
                            </div>
                          )}
                          {m.profitLabel ? (
                            <div className="flex justify-between items-center pt-1 border-t border-slate-800 text-[11px] text-amber-400">
                              <span className="text-[10px] text-slate-400 font-sans">{t('dollar.m_profit')}</span>
                              <span className="font-black dir-ltr text-right">
                                {m.profitLabel} {t('dollar.iqd')}
                              </span>
                            </div>
                          ) : null}
                        </div>

                        {m.note && <div className="text-[10px] text-slate-500 dark:text-slate-400 font-bold mt-1 bg-slate-50 dark:bg-[#161b22] p-2 rounded-lg">📝 {m.note}</div>}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </motion.div>
        )}

        {subTab === 'trusts' && (
          <motion.div
            key="subtab-trusts"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="w-full space-y-6"
          >
            <div className="premium-card p-4 sm:p-6 space-y-4 sm:space-y-5 border border-gray-150 dark:border-[#30363d] shadow-lg">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                  <h4 className="text-sm sm:text-base font-black text-gray-900 dark:text-[#f0f6fc] uppercase tracking-wider flex items-center gap-2">
                    <span>💸</span>
                    <span>{t('dollar.trusts_portfolio')}</span>
                  </h4>
                  <p className="text-[11px] sm:text-xs text-gray-400 font-bold mt-0.5">{t('dollar.trusts_total', { total: cards.trustsTotalLabel, n: cards.trustsCount })}</p>
                </div>

                <div className="relative w-full sm:w-72">
                  <input
                    type="text"
                    value={props.search || ''}
                    onChange={(e) => props.onSearchChange(e.target.value)}
                    className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-2xl pl-10 pr-4 py-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:text-[#f0f6fc] font-bold"
                    placeholder={t('dollar.trust_search')}
                  />
                  <Search size={16} className="absolute left-3.5 top-3 text-gray-400 dark:text-[#8b949e]" />
                </div>
              </div>

              {/* Trusts Grid */}
              <div className="border border-gray-150 dark:border-[#30363d] rounded-2xl overflow-hidden bg-white dark:bg-[#0d1117] min-h-[300px] max-h-[500px] overflow-y-auto custom-scrollbar p-3 sm:p-4 space-y-3">
                {props.trusts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-12 text-center h-full">
                    <span className="text-5xl mb-3">🤷‍♂️</span>
                    <p className="text-xs font-black text-gray-400 dark:text-gray-500 leading-relaxed max-w-sm">{t('dollar.trusts_empty')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {props.trusts.map((d) => (
                      <div key={d.id} className="p-3.5 sm:p-4 rounded-2xl border border-slate-200/80 dark:border-[#30363d] bg-gray-50/50 dark:bg-[#161b22]/50 hover:border-emerald-500/40 transition-all space-y-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <h5 className="font-black text-xs sm:text-sm text-gray-900 dark:text-[#f0f6fc]">{d.name}</h5>
                            <p className="text-[10px] text-gray-400 font-bold mt-0.5">{t('dollar.trust_balance')}</p>
                          </div>
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-black bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 border border-emerald-200/50 dark:border-emerald-900/30">{t('dollar.trust_active')}</span>
                        </div>

                        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-end gap-2.5 pt-2 border-t border-gray-200/60 dark:border-[#30363d]">
                          <div className="bg-slate-900 text-white dark:bg-black p-2.5 rounded-xl border border-slate-800 text-right">
                            <span className="text-[10px] text-slate-400 block mb-0.5">{t('dollar.trust_amount')}</span>
                            <div className="text-lg sm:text-xl font-black text-amber-400 font-mono dir-ltr">${d.usdLabel}</div>
                          </div>

                          <button
                            type="button"
                            onClick={() => props.onSellTrust(d.id)}
                            className="w-full sm:w-auto px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-xl text-xs transition-all active:scale-98 flex items-center justify-center gap-1.5 shadow-md shadow-emerald-600/20 cursor-pointer min-h-[42px]"
                          >
                            <span>💸</span>
                            <span>{t('dollar.sell_this')}</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
